import { useCompanionStore } from "@/stores/companion-store";
/**
 * Screenshot-attach helper for the desktop input bar and dictation flow.
 *
 * Encapsulates the auto-attach pipeline so both the text path and voice path
 * (`use-dictation`) share a single, well-tested entry point.
 *
 * Responsibilities:
 *   1. Resolve the active agent's model and gate on vision capability.
 *   2. Capture the relevant display via the Tauri command (cursor by default).
 *   3. Stash per-screen metadata for inverse `[POINT:...]` resolution.
 *   4. Upload the JPEG file(s) to the backend.
 *   5. Optionally create one image frame per upload on the canvas.
 *   6. Build a `[POINTER:screen=1,x=...,y=...]` token in screen-local coords
 *      so the agent knows what the user was pointing at.
 *
 * `intent === false` short-circuits to the empty result without any side
 * effects — callers can use a single conditional regardless of whether
 * the user requested screenshot attachment for this turn.
 */
import { uploadImage } from "@/api/uploads";
import { useAgentStore } from "@/stores/agent-store";
import { resolveTemplateModel } from "@/lib/model-registry";
import { modelSupportsVision } from "@/lib/model-vision";
import {
  captureScreenshotDisplayAsFile,
  captureScreenshotsAsFiles,
  type CaptureScope,
  type ScreenshotPreview,
} from "./use-screenshot-capture";
import {
  registerOutboundScreensForImages,
  setOutboundScreens,
} from "./outbound-screenshot-metadata";
import {
  createImageFramesForUploads,
  type UploadedImage,
} from "./use-image-frame-creation";
import { useScreenshotSettings } from "./screenshot-settings";
import { SCREEN_RECORDING_PERMISSION_NEEDED_EVENT } from "./screen-recording-permission-event";
import { buildScreenshotPointerToken } from "./screenshot-pointer";
import { displayRosterToken } from "./display-roster-token";
import type { ScreenshotScreenMeta } from "@/lib/pointing-transform";

export interface ScreenshotAttachResult {
  imageIds: string[];
  pointerToken: string | null;
  displaysToken: string | null;
  uploads: UploadedImage[];
  screens: ScreenshotScreenMeta[];
  previews: ScreenshotPreview[];
}

export type ScreenshotCaptureTarget = CaptureScope | { displayId: number };

const EMPTY: ScreenshotAttachResult = {
  imageIds: [],
  pointerToken: null,
  displaysToken: null,
  uploads: [],
  screens: [],
  previews: [],
};

function resolveActiveAgentModel(): string | undefined {
  const { agents, selectedAgentId, templates } = useAgentStore.getState();
  const activeAgent = agents.find((a) => a.id === selectedAgentId);
  const template = activeAgent
    ? templates.find((t) => t.id === activeAgent.agent_template)
    : undefined;
  return resolveTemplateModel(
    template,
    useCompanionStore.getState().availableModels,
  ).model;
}

export function activeAgentSupportsScreenshotAttachment(): boolean {
  return modelSupportsVision(resolveActiveAgentModel());
}

export async function attachScreenshotIfRequested(
  intent: boolean,
  pointerOverride?: { x: number; y: number },
  target: ScreenshotCaptureTarget = "cursor_screen",
): Promise<ScreenshotAttachResult> {
  if (!intent) return EMPTY;

  if (!activeAgentSupportsScreenshotAttachment()) {
    console.debug(
      "[screenshot-attach] active agent's model lacks vision; skipping capture",
    );
    return EMPTY;
  }

  try {
    const { invoke } = await import("@tauri-apps/api/core");
    const status = await invoke<{ screenRecording?: string }>(
      "permissions_check_screen_recording",
    );
    // "unknown" is a check that could not run yet, not a denial.
    if (
      status?.screenRecording !== "granted" &&
      status?.screenRecording !== "unknown"
    ) {
      window.dispatchEvent(new Event(SCREEN_RECORDING_PERMISSION_NEEDED_EVENT));
      return EMPTY;
    }
  } catch {
    // If the helper is unavailable, let the existing capture path surface the
    // failure so dev builds do not silently diverge from production.
  }

  const captured =
    typeof target === "string"
      ? await captureScreenshotsAsFiles({ scope: target })
      : await captureScreenshotDisplayAsFile(target.displayId).then(
          (result) => ({
            files: result.file
              ? [
                  new File([result.file], "screen-1-of-1.jpg", {
                    type: result.file.type,
                    lastModified: result.file.lastModified,
                  }),
                ]
              : [],
            screens: result.screen ? [result.screen] : [],
            previews: result.preview ? [result.preview] : [],
            cursorX: result.cursorX,
            cursorY: result.cursorY,
          }),
        );
  const { files, screens, previews = [], cursorX, cursorY } = captured;
  if (files.length === 0 || screens.length === 0) return EMPTY;

  setOutboundScreens(screens);
  console.debug(
    "[screenshot-attach] capture metadata",
    screens.map((screen) => ({
      index: screen.index,
      displayId: screen.displayId,
      origin: [screen.originX, screen.originY],
      native: [screen.widthPx, screen.heightPx],
      scaled: [screen.scaledWidth, screen.scaledHeight],
      scaleFactor: screen.scaleFactor,
    })),
    { cursor: [Math.round(cursorX), Math.round(cursorY)] },
  );

  const uploadResults = await Promise.all(files.map((f) => uploadImage(f)));
  const uploads: UploadedImage[] = uploadResults.map((u) => ({
    id: u.id,
    url: u.url,
    width: u.width,
    height: u.height,
    filename: u.original_filename || undefined,
  }));
  const imageIds = uploads.map((u) => u.id);
  registerOutboundScreensForImages(imageIds, screens);

  if (useScreenshotSettings.getState().copySentImagesToCanvas) {
    await createImageFramesForUploads(uploads);
  }

  // Voice callers provide the cursor location captured when Speak began.
  // The physical cursor may have moved to another display by the time speech
  // finishes and screenshots are uploaded, but that later movement must not
  // rewrite the user's original pointing hint.
  const pointerX = pointerOverride?.x ?? cursorX;
  const pointerY = pointerOverride?.y ?? cursorY;
  const pointerToken = buildScreenshotPointerToken(screens, pointerX, pointerY);
  console.debug("[screenshot-attach] pointer token", {
    pointerToken,
    cursor: [Math.round(pointerX), Math.round(pointerY)],
    displayCount: screens.length,
  });

  // The prompt carries only the captured display. With more than one
  // connected, say so: the header in front of the image reads "screen=1 of 1",
  // which otherwise tells the runtime its desktop is a single screen.
  const displaysToken = await displayRosterToken(
    screens.map((screen) => screen.displayId),
  );

  return { imageIds, pointerToken, displaysToken, uploads, screens, previews };
}
